// Shared parsing of the step 1 form fields declared in the project content file
// (public/content/{projectId}.json -> exercise_tiles_survey.step1.fields).
//
// The survey exercise uses these to render the form, the results components use
// them to build the filters and to label each response, so both stay in step
// with whatever the content file declares.

const OPTION_TYPES = ["select", "radio", "checkbox"]

// "filter": true (or "true") marks a field as a results filter
function isTruthy(value) {
  return value === true || value === "true"
}

export function normaliseField(field, index) {
  const key = field.key || field.name || field.id || `field${index + 1}`
  const label = field.label || field.title || ""
  const type = field.type || "text"
  const required = isTruthy(field.required)
  const options = (field.options || field.items || []).map((option) =>
    typeof option === "string" ? { title: option } : option
  )

  const withOptions = OPTION_TYPES.indexOf(type) !== -1
  const multiple = type === "checkbox" && options.length > 0

  // an "other" option reveals a free text field, stored as `{key}Other`
  const allowOther = withOptions && isTruthy(field.allowOther)
  const otherTitle = field.otherTitle || "Other"
  if (allowOther && !options.some((option) => option.title === otherTitle)) {
    options.push({ title: otherTitle, id: `${key}-other` })
  }

  const otherLabel = field.otherLabel || "Please specify"

  return {
    key,
    type,
    label,
    heading: field.heading || null,
    // an optional short label for the results, where a full question is too long
    resultsTitle: field.resultsTitle || null,
    required,
    options,
    multiple,
    allowOther,
    otherTitle,
    otherKey: field.otherKey || `${key}Other`,
    otherLabel,
    // only fields flagged in the content file filter the results
    filter: isTruthy(field.filter),
    // rules are built here (not per render) so vuetify does not re-validate on every tick
    rules: required
      ? [
          (v) =>
            (multiple ? Array.isArray(v) && v.length > 0 : !!v) ||
            `${label || "This field"} is required`,
        ]
      : [],
    // stable empty array, so a checkbox group only shows one error message
    noRules: [],
    otherRules: [(v) => !!v || `${otherLabel} is required`],
  }
}

// Reads step1.fields, falling back to the legacy content shape
// (question1 / question2 / question3 + regions + categories).
export function stepFields(step) {
  const source = step || {}

  if (Array.isArray(source.fields) && source.fields.length) {
    return source.fields.map(normaliseField)
  }

  const legacy = [
    { key: "firstName", label: source.question1 || "First name" },
    { key: "lastName", label: source.question2 || "Last name" },
  ]
  if (source.question3) {
    legacy.push({ key: "jobTitle", label: source.question3 })
  }
  if (source.regions) {
    legacy.push({
      key: "region",
      type: "select",
      heading: "Where are you based?",
      label: "Select a region from the list *",
      required: true,
      filter: true,
      options: source.regions,
    })
  }
  if (source.categories) {
    legacy.push({
      key: "category",
      type: "select",
      heading: "Categories",
      label: "Select a category from the list *",
      required: true,
      filter: true,
      options: source.categories,
    })
  }
  return legacy.map(normaliseField)
}

export function filterFields(fields) {
  return (fields || []).filter((field) => field.filter)
}

export function fieldTitle(field) {
  return field.resultsTitle || field.heading || field.label || field.key
}

// Items for a results filter dropdown
export function filterItems(field) {
  if (field.type === "checkbox" && !field.multiple) return ["Yes", "No"]
  return field.options.map((option) => option.title)
}

function readKey(doc, key) {
  if (!doc) return null
  const details = doc.details || {}
  const value = details[key] === undefined ? doc[key] : details[key]
  return value === undefined ? null : value
}

// The stored answer, used for filtering
export function rawValue(doc, field) {
  return readKey(doc, field.key)
}

// The answer as shown in the results, resolving "other" free text
export function displayValue(doc, field) {
  const value = rawValue(doc, field)
  const other = field.allowOther ? readKey(doc, field.otherKey) : null
  const resolve = (item) =>
    item === field.otherTitle && other ? `${field.otherTitle}: ${other}` : item

  if (Array.isArray(value)) return value.map(resolve).join(", ")
  if (typeof value === "boolean") return value ? "Yes" : "No"
  if (value === null || value === "") return ""
  return resolve(value)
}

export function matchesFilter(doc, field, selected) {
  if (!selected) return true
  const value = rawValue(doc, field)
  if (Array.isArray(value)) return value.indexOf(selected) !== -1
  if (typeof value === "boolean") return (value ? "Yes" : "No") === selected
  return value === selected
}

// Keys the exercise itself writes - everything else on a response is an answer
const SYSTEM_KEYS = [
  "id",
  "uid",
  "pid",
  "details",
  "selectedTiles",
  "cols",
  "step",
  "gridSlots",
  "gridCols",
  "cellSize",
  "createdOn",
  "updatedOn",
]

// "gender_other" -> "Gender other", "jobTitle" -> "Job title"
export function humanise(key) {
  return key
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .replace(/^./, (character) => character.toUpperCase())
}

function plainText(value) {
  if (Array.isArray(value)) {
    const items = value.filter(
      (item) => typeof item === "string" || typeof item === "number"
    )
    return items.length === value.length ? items.join(", ") : ""
  }
  if (typeof value === "boolean") return value ? "Yes" : "No"
  if (typeof value === "number") return String(value)
  if (typeof value === "string") return value
  return ""
}

// Every answer held against a response: the fields the content file declares,
// followed by anything else stored on the doc - a field that has since been
// renamed or dropped from the content, or an answer saved by an earlier version
// of the form - so a filled in field is never silently missing from the results.
export function answers(doc, fields) {
  const list = []
  const seen = []

  ;(fields || []).forEach((field) => {
    seen.push(field.key)
    if (field.allowOther) seen.push(field.otherKey)

    const value = displayValue(doc, field)
    if (value === "") return
    list.push({
      key: field.key,
      title: fieldTitle(field),
      value,
      chip: field.type !== "text" && field.type !== "textarea",
      extra: false,
    })
  })

  if (!doc) return list

  ;[doc.details || {}, doc].forEach((store) => {
    Object.keys(store).forEach((key) => {
      if (SYSTEM_KEYS.indexOf(key) !== -1) return
      if (seen.indexOf(key) !== -1) return
      seen.push(key)

      const value = plainText(store[key])
      if (value === "") return

      // pair an undeclared answer with its own "other" free text, the way a
      // declared field does
      let suffixed = ""
      ;[`${key}Other`, `${key}_other`].forEach((otherKey) => {
        if (seen.indexOf(otherKey) !== -1) return
        const text = plainText(store[otherKey])
        if (text === "") return
        seen.push(otherKey)
        suffixed = text
      })

      list.push({
        key,
        title: humanise(key),
        value: suffixed ? `${value}: ${suffixed}` : value,
        chip: false,
        extra: true,
      })
    })
  })

  return list
}
